import React, { useEffect } from 'react';
import { BackButton, Input, ErrorAlert, Checkbox, Button, ErrorMessage } from '../Components/Simple';
import { ApiError, EntryTable, EntryTableColumn } from '../Components/Combined';
import { FQHC as modelType } from '../Constants/modelTypes';
import useSessionState from '../Hooks/useSessionState';
import { useNavigate, useLocation } from 'react-router-dom';
import useCollection from '../Hooks/useCollection';
import { formatDateStringIfMatches, getDefaultDateString, formatStringAsCurrency } from '../Utils/stringUtils';
import useCalendarYear from '../Hooks/useCalendarYear';
import useExportRequest from '../Hooks/useExportRequest';
import useImportRequest from '../Hooks/useImportRequest';
import { mapToSetters } from '../Utils/mapping';
import FilePickerButton from '../Components/Simple/FilePickerButton';
import { API_DATE_STRING_FORMAT, DATE_STRING_FORMAT } from '../Constants/stringFormats';
import { transformStringToUpCase } from '../Utils/valueTransforms';
import useAsyncPricerApi from '../Hooks/useAsyncPricerApi';
import { validateBilled } from '../Utils/validation';

export default function FQHC() {
    const location = useLocation();
    const { prePopulatedProviderNPI } = location.state || {};
    const [claimId, setClaimId] = useSessionState('', 'claimId');
    const [providerNPI, setProviderNPI] = useSessionState(prePopulatedProviderNPI ?? '', 'providerNPI');
    const [providerZipCode, setProviderZipCode] = useSessionState('', 'providerZipCode');
    const [fromDate, setFromDate] = useSessionState(getDefaultDateString(), 'fromDate');
    const [toDate, setToDate] = useSessionState(getDefaultDateString(), 'toDate');
    const [calendarYear, setCalendarYearInputDate] = useCalendarYear(null);
    const pricerName = 'Federally Qualified Health Center';

    const navigate = useNavigate();

    const getNewLine = (index) => {
        return {
            lineId: index + 1,
            revenueCode: '',
            procedureCode: '',
            billed: ''
        };
    };

    const getDefaultLines = () => {
        return [getNewLine(0), getNewLine(1), getNewLine(2), getNewLine(3), getNewLine(4)];
    };

    const linesCollection = useCollection(getDefaultLines(), getNewLine, 'lines');

    const [fileExporting, _fileExportSuccess, fileExportError, fileExportExecute, fileExportReset] = useExportRequest(modelType, {
        claimId,
        providerNPI,
        providerZipCode,
        fromDate,
        toDate,
        lines: linesCollection.values,
    });

    const [fileImporting, fileImportSuccess, fileImportError, fileImportResult, fileImportReset, fileImportOnChange] = useImportRequest(modelType);

    const pricer = useAsyncPricerApi(modelType);

    const handleSubmit = async () => {
        fileExportReset();
        fileImportReset();
        await pricer.execute(formatRequest());
    };

    const handleFileSave = () => {
        pricer.reset();
        fileExportExecute();
    };

    const handleLoadOnChange = (e) => {
        pricer.reset();
        fileImportOnChange(e);
    };

    const formatRequest = () => {
        return {
            claimId: claimId || null,
            providerId: providerNPI || null,
            providerZip: providerZipCode || null,
            fromDate: formatDateStringIfMatches(fromDate || null, DATE_STRING_FORMAT, API_DATE_STRING_FORMAT),
            toDate: formatDateStringIfMatches(toDate || null, DATE_STRING_FORMAT, API_DATE_STRING_FORMAT),
            lines: linesCollection.values.map(l => {
                return {
                    lineNumber: !l.lineId && l.lineId !== 0 ? null : l.lineId.toString(),
                    revCode: l.revenueCode || null,
                    HCPCS: l.procedureCode || null,
                    billed: l.billed,
                }
            })
        };
    }
    const handleReset = () => {
        setClaimId('');
        setProviderNPI('');
        setProviderZipCode('');
        setFromDate(getDefaultDateString());
        setToDate(getDefaultDateString());
        linesCollection.setValues(getDefaultLines());
        pricer.reset();
        fileImportReset();
        fileExportReset();
    };

    useEffect(() => {
        setCalendarYearInputDate(toDate);
    }, [toDate]);


    useEffect(() => {
        if (fileImportSuccess) {
            const setters = {
                claimId: setClaimId,
                providerNPI: setProviderNPI,
                providerZipCode: setProviderZipCode,
                fromDate: setFromDate,
                toDate: setToDate,
                lines: linesCollection.setValues,
            };
            handleReset();
            mapToSetters(fileImportResult, setters);
        }
    }, [fileImportSuccess]);

    useEffect(() => {
        if (pricer.success) {
            navigate('result', {
                state: {
                    calendarYear,
                    result:  pricer.result,
                }
            });
        }
    }, [pricer.success]);

    return (
        <div className="py-2">
            <div className="row my-2 justify-content-between sticky top g-0 border-bottom align-items-center">
                <div className="col-auto">
                    <h3>{pricerName} Pricer</h3>
                    <div className="fs-6">
                        Enter all claim information requested below. Please press SUBMIT when complete to calculate the prospective payment amount.
                    </div>
                </div>
                <div className="col-auto">
                    <BackButton />
                </div>
            </div>
            {(pricer.error || fileImportError || fileExportError) && (<div>
                <ApiError error={pricer.error} />
                {fileImportError ? <ErrorMessage errors={[{ message: fileImportError }]} /> : null}
                {fileExportError ? <ErrorMessage errors={[{ message: fileExportError }]} /> : null}
            </div>)}
            <form onSubmit={handleSubmit}>
                <div className="row my-2">
                    <div className="col-md-3">
                        <Input id="claim-id" labelClassName="fw-bold" label="Claim ID" onChange={setClaimId} value={claimId} />
                    </div>
                    <div className="col-md-4">
                        <Input label="Provider NPI" labelClassName="fw-bold" id="provider-npi" onChange={setProviderNPI} value={providerNPI} maxLength={10} />
                    </div>
                </div>
                <div className="row my-2">
                    <div className="col-md-3">
                        <Input label="Provider's Zip Code" maxLength={5} labelClassName="fw-bold" id="providers-zip-code" onChange={setProviderZipCode} value={providerZipCode} />
                    </div>
                    <div className="col-md-3">
                        <Input label="From Date" labelClassName="fw-bold" id="from-date" type="date" onChange={setFromDate} value={fromDate} placeholder="MM/DD/YYYY" />
                    </div>
                    <div className="col-md-3">
                        <Input label="To Date" labelClassName="fw-bold" id="to-date" type="date" onChange={setToDate} value={toDate} placeholder="MM/DD/YYYY" />
                    </div>
                </div>
                <hr />
                <div className="row g-0">
                    <div className="col my-2">
                        <EntryTable id="service-line-items"
                            collection={linesCollection}
                            lineCountLabel="Total Lines"
                            upDownNavigation={true}
                            max={100}
                            runningTotal={{
                                name: 'billed',
                                className: 'me-2',
                                formatter: formatStringAsCurrency
                            }}>
                            <EntryTableColumn label="Line ID"
                                name="lineId"
                                type="label"
                                className="mw-50" />
                            <EntryTableColumn label="Revenue Code"
                                name="revenueCode"
                                type="text" />
                            <EntryTableColumn label="HCPCS Code"
                                name="procedureCode"
                                type="text"
                                valueTransform={transformStringToUpCase} />
                            <EntryTableColumn label="Billed"
                                name="billed"
                                type="text"
                                className="mw-100 text-end"
                                onValidate={validateBilled} />
                        </EntryTable>
                    </div>
                </div>
                <div className="row justify-content-between my-2 sticky bottom g-0 border-top">
                    <div className="col-auto align-self-center">
                        <div className="row g-0">
                            <div className="col-auto ms-2">
                                <Button className="btn-outline-secondary" id="save-request-button"
                                    disabled={pricer.loading || fileImporting || fileExporting}
                                    onClick={handleFileSave}
                                    text="Save" />
                            </div>
                            <div className="col-auto ms-2">
                                <FilePickerButton id="load-request" className="btn-outline-secondary"
                                    loading={fileImporting}
                                    disabled={pricer.loading || fileImporting || fileExporting}
                                    onChange={handleLoadOnChange}
                                    text="Load"
                                    accepts=".mrpreq" />
                            </div>
                        </div>
                    </div>
                    <div className="col-auto align-self-center">
                        <ErrorAlert visible={pricer.error} />
                    </div>
                    <div className="col-auto align-self-center">
                        <div className="row g-0 justify-cotent-end">
                            <div className="col-auto">
                                <Button className="btn-outline-secondary" onClick={handleReset} disabled={pricer.loading || fileImporting || fileExporting} text="Reset" />
                            </div>
                            <div className="col-auto ms-2">
                                <Button className="btn-primary" disabled={pricer.loading || fileImporting || fileExporting} loading={pricer.loading} text="Submit" onClick={handleSubmit} />
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
}
